import React, { useEffect, useState, Fragment } from "react";
import postRequests from "api/post";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setLoyaltyUser } from "features/slices/loyaltySlice";
import { MdOutlineStoreMallDirectory } from "react-icons/md";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { MdVisibilityOff, MdVisibility } from "react-icons/md";
import Blob from "./Blob";
import { Loader } from "components";
import { SocialMediaIcons } from "components/SocialMedia";
import PropTypes from "prop-types";
import "../loyalty.css";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

LoyaltyRegister.propTypes = {
  merchantDetails: PropTypes.object,
  customerDetails: PropTypes.object,
  customerPhoneNumberFromURL: PropTypes.string,
  recentOrderId: PropTypes.string,
  userShouldEnterBioData: PropTypes.bool,
  handleShowLogin: PropTypes.func,
  merchantTheme: PropTypes.object,
};

LoyaltyRegister.defaultProps = {
  merchantDetails: {},
  customerDetails: {},
  customerPhoneNumberFromURL: "",
  recentOrderId: "",
  userShouldEnterBioData: false,
  handleShowLogin: () => {},
  merchantTheme: {},
};

export default function LoyaltyRegister(props) {
  const {
    merchantDetails,
    customerDetails,
    customerPhoneNumberFromURL,
    recentOrderId,
    userShouldEnterBioData,
    handleShowLogin,
    merchantTheme,
  } = props;
  const [loading, setLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [merchantId, setMerchantId] = useState(
    merchantDetails?.merchant_id || null
  );
  const [orderId, setOrderId] = useState(recentOrderId || null);
  const [phone, setPhone] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [showPlaceholderIcon, setShowPlaceholderIcon] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const searchParams = new URLSearchParams(location.search);
  const phoneFromURL = searchParams.get("phone");

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      fullName: "",
      email: "",
      birthDay: "",
      birthMonth: "",
      phone: phone,
      password: "",
      confirmPassword: "",
    },
  });

  useEffect(() => {
    let phoneValue =
      customerPhoneNumberFromURL || customerDetails?.customer_phone || "";
    let user = null;

    try {
      user = sessionStorage.getItem("LOYALTY_USER");
    } catch {
      user = null;
    }

    if (!phoneValue) setIsNewUser(true);

    if ((user && !phoneValue) || !phoneValue) {
      try {
        const parsed = JSON.parse(user);
        setMerchantId(parsed?.merchantId);
        setOrderId(parsed?.recentOrderId);
        if (!customerPhoneNumberFromURL) {
          phoneValue = parsed?.customer_phone || parsed._phone;
        }
      } catch {
        setMerchantId(merchantDetails?.merchant_id || null);
        phoneValue =
          customerPhoneNumberFromURL || customerDetails?.customer_phone || "";
      }
    } else {
      setMerchantId(merchantDetails?.merchant_id || null);
      phoneValue =
        customerPhoneNumberFromURL || customerDetails?.customer_phone || "";
    }

    setPhone(phoneValue);
    setValue("phone", phoneValue);

    if (merchantDetails) {
      sessionStorage.setItem("MERCHANT", JSON.stringify(merchantDetails));
      sessionStorage.setItem("MERCHANT_THEME", JSON.stringify(merchantTheme));
    }
    if (customerDetails) {
      const userData = {
        ...customerDetails,
        merchantId: merchantDetails?.merchant_id,
        recentOrderId,
        merchantCode: merchantDetails?.merchant_code,
      };
      sessionStorage.setItem("LOYALTY_USER", JSON.stringify(userData));
      dispatch(setLoyaltyUser(userData));
    } else {
      setIsNewUser(true);
    }

    setIsInitialized(true);
  }, [
    merchantDetails,
    customerDetails,
    customerPhoneNumberFromURL,
    merchantTheme,
    recentOrderId,
    setValue,
  ]);

  const onSubmitNewUser = async (data) => {
    setLoading(true);
    const monthNum = MONTHS.findIndex((m) => m === data.birthMonth) + 1;
    const birthMonth = monthNum.toString().padStart(2, "0");

    try {
      const payload = {
        cus_name: customerDetails?.customer_name || data.fullName,
        cus_pass: data.password,
        cus_email: customerDetails?.customer_email || data.email,
        cus_phone: data.phone,
        cus_dob:
          customerDetails?.customer_dob || `${data.birthDay}-${birthMonth}`,
        cus_merchant: merchantId,
      };
      const apiData = await postRequests.registerLoyaltyUser(payload);
      const statusNum = Number(apiData?.status);

      if (statusNum === 92 || statusNum === 0) {
        toast.success(
          apiData?.message ||
            "Registration successful! Please verify your account.",
          { appearance: "success", autoDismiss: 2000 }
        );
        navigate(
          `/${merchantTheme?.slug}/verify?phone=${encodeURIComponent(
            data.phone
          )}&merchant=${encodeURIComponent(merchantId)}`
        );
      } else if (statusNum === 91) {
        toast.error(apiData?.message || "User already exists. Please login.");
        handleShowLogin();
      } else {
        toast.error(apiData?.message || "Registration failed.");
      }
    } catch (err) {
      toast.error("Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isInitialized) {
    return <Loader text="Loading..." />;
  }

  return (
    <div
      className="loyalty-section min-h-screen flex flex-col lg:flex-row bg-white"
      data-testid="loyalty-register"
    >
      <Blob />

      <div
        className={`flex justify-center items-center lg:hidden inset-0 ${
          merchantTheme?.theme || "bg-brandBlue"
        } z-0 w-full py-20 px-10`}
        style={{
          backgroundImage:
            merchantTheme?.auth_banner !==
            "https://payments.ipaygh.com/app/webroot/img/stores/null"
              ? `url("${merchantTheme?.auth_banner}")`
              : 'url("/svgs/gift.svg")',
          backgroundSize: merchantTheme?.auth_banner ? "cover" : "500px 500px",
          backgroundColor: merchantTheme?.auth_banner
            ? merchantTheme?.theme
            : "",
          backgroundRepeat: merchantTheme?.auth_banner ? "no-repeat" : "",
          backgroundPosition: merchantTheme?.auth_banner ? "center" : "",
        }}
      >
        <div className="absolute inset-0 bg-black-900 bg-opacity-50 z-0"></div>

        <div className="w-12 h-12 bg-gray-200 border-2 border-gray-200 rounded-full flex items-center justify-center mr-4 overflow-hidden z-50">
          {!showPlaceholderIcon ? (
            <img
              src={merchantDetails?.merchant_logo}
              alt={`${merchantDetails?.merchant_name || "Merchant"} logo`}
              className="w-full h-full object-cover"
              onError={(e) => {
                setShowPlaceholderIcon(true);
              }}
            />
          ) : (
            <MdOutlineStoreMallDirectory
              size={50}
              color={merchantTheme?.theme}
              className={` ${merchantTheme?.theme || "text-gray-500"}`}
            />
          )}
        </div>

        <h2 className="text-2xl font-extrabold mb-3 tracking-tight text-white-A700 drop-shadow text-center">
          {merchantDetails?.merchant_name}
        </h2>
      </div>

      <div className="flex-1 flex justify-center lg:items-center bg-white p-8 z-50 bg-white-A700">
        <div className="w-full max-w-md">
          <h2 className="text-3xl font-bold mt-8 mb-2 text-gray-800 text-center">
            Sign Up
          </h2>
          <p className="text-center text-gray-500 mb-2">
            Enter your mobile number and preferred password to get started.
          </p>
          <div
            onClick={() => navigate(`/${merchantTheme?.slug}/how-it-works`)}
            className="text-gray-600 text-sm underline hover:cursor-pointer hover:text-brandBlue hover:underline transition-colors flex justify-center items-center mb-8"
          >
            <p className="text-center">How it works?</p>
          </div>
          <form className="space-y-4" onSubmit={handleSubmit(onSubmitNewUser)}>
            {/*-- If New User (i.e no phonenumber included in route params), then show full name and email -- otherwise, show only mobile number */}
            {(isNewUser || userShouldEnterBioData) && (
              <Fragment>
                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brandBlue ${
                      errors.fullName ? "border-red-500" : ""
                    }`}
                    placeholder="Full Name"
                    {...register("fullName", {
                      required: "Full name is required",
                    })}
                  />
                  {errors.fullName && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.fullName.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brandBlue ${
                      errors.email ? "border-red-500" : ""
                    }`}
                    placeholder="Email Address"
                    {...register("email")}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>
              </Fragment>
            )}

            <div>
              <label className="block text-sm font-semibold mb-1">
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-brandBlue transition-colors ${
                  errors.phone
                    ? "border-red-500 focus:ring-1 focus:ring-red-500 bg-red-50"
                    : "border-gray-300"
                } ${phoneFromURL ? "bg-gray-200" : ""}`}
                placeholder="Enter Mobile Number"
                {...register("phone", {
                  required: "Mobile number is required",
                })}
                disabled={loading || phoneFromURL}
              />
              {errors.phone && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.phone.message}
                </p>
              )}
            </div>

            {(isNewUser || userShouldEnterBioData) && (
              <div className="flex space-x-2">
                <div className="flex-1">
                  <label className="block text-sm font-semibold mb-1">
                    Birth Day
                  </label>
                  <select
                    className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brandBlue ${
                      errors.birthDay ? "border-red-500" : ""
                    }`}
                    {...register("birthDay")}
                  >
                    <option value="">Day</option>
                    {[...Array(31)].map((_, i) => {
                      const day = (i + 1).toString().padStart(2, "0");
                      return (
                        <option key={day} value={day}>
                          {day}
                        </option>
                      );
                    })}
                  </select>
                  {errors.birthDay && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.birthDay.message}
                    </p>
                  )}
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-semibold mb-1">
                    Birth Month
                  </label>
                  <select
                    className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brandBlue ${
                      errors.birthMonth ? "border-red-500" : ""
                    }`}
                    {...register("birthMonth")}
                  >
                    <option value="">Month</option>
                    {MONTHS.map((month, i) => (
                      <option key={month} value={month}>
                        {month}
                      </option>
                    ))}
                  </select>
                  {errors.birthMonth && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.birthMonth.message}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold mb-1">
                Create Password <span className="text-red-500">*</span>
              </label>
              <div className="relative mb-2">
                <input
                  type={showPassword ? "text" : "password"}
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-brandBlue transition-colors pr-10 ${
                    errors.password
                      ? "border-red-500 focus:ring-1 focus:ring-red-500 bg-red-50"
                      : "border-gray-300"
                  }`}
                  placeholder="Create Password"
                  {...register("password", {
                    required: "Password is required",
                  })}
                  disabled={loading}
                  autoComplete="current-password"
                />

                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex="-1"
                >
                  {showPassword ? (
                    <MdVisibilityOff className="h-5 w-5" />
                  ) : (
                    <MdVisibility className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.password.message}
                </p>
              )}

              <label className="block text-sm font-semibold mb-1">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <div className="relative mb-2">
                <input
                  type={showPassword ? "text" : "password"}
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-brandBlue transition-colors pr-10 ${
                    errors.password
                      ? "border-red-500 focus:ring-1 focus:ring-red-500 bg-red-50"
                      : "border-gray-300"
                  }`}
                  placeholder="Confirm Password"
                  {...register("confirmPassword", {
                    required: "Confirm Password is required",
                    validate: (value) =>
                      value === watch("password") || "Passwords do not match",
                  })}
                  disabled={loading}
                  autoComplete="current-password"
                />

                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex="-1"
                >
                  {showConfirmPassword ? (
                    <MdVisibilityOff className="h-5 w-5" />
                  ) : (
                    <MdVisibility className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.confirmPassword.message}
                </p>
              )}

              <div className="flex justify-end">
                <a
                  href={`/${merchantTheme?.slug}/forgot-password`}
                  className="text-right text-gray-600 text-sm underline hover:cursor-pointer hover:text-brandBlue hover:underline transition-colors"
                >
                  Forgot password?
                </a>
              </div>
            </div>

            <button
              type="submit"
              className={`w-full py-3 rounded font-semibold text-lg mt-2 transition-all duration-200 ${
                merchantTheme?.theme || "bg-brandBlue"
              } text-gray-100`}
              style={{ backgroundColor: merchantTheme?.theme }}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Registering...
                </div>
              ) : (
                "Continue"
              )}
            </button>

            <div className="flex flex-col items-center space-y-3 mt-4">
              <span
                onClick={handleShowLogin}
                className={`text-lg font-semibold hover:underline transition-colors cursor-pointer ${
                  merchantTheme?.theme || "text-brandBlue"
                }`}
                style={{ color: merchantTheme?.theme }}
              >
                I already have an account
              </span>
            </div>
          </form>
          {/* Social Media Icons */}
          <SocialMediaIcons
            socialMediaData={merchantTheme?.social_media}
            merchantTheme={merchantTheme}
          />
        </div>
      </div>
    </div>
  );
}
